import { useEffect, useMemo } from "react"
import { useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"

import { useDebounce } from "shared/lib/performance/useDebounce"
import { SelectOption } from "shared/types/Select"

import { ProjectHistoryFilterState } from "../ProjectHistoryFilter"

const defaultProjectHistoryFilterState: ProjectHistoryFilterState = {
  timeFrame: 30,
  searchTerm: ""
}

export function useProjectHistoryFilterFormViewModel({
  onChange
}: {
  onChange: (state: ProjectHistoryFilterState) => void
}) {
  const { t } = useTranslation()
  const form = useForm<ProjectHistoryFilterState>({
    defaultValues: defaultProjectHistoryFilterState
  })

  const formValues = form.watch()
  const debouncedSearchTerm = useDebounce(formValues.searchTerm)

  const filterValues = useMemo(
    () => ({
      timeFrame: formValues.timeFrame,
      searchTerm: debouncedSearchTerm
    }),
    [formValues.timeFrame, debouncedSearchTerm]
  )

  useEffect(() => {
    onChange(filterValues)
  }, [filterValues, onChange])

  const timeFrameOptions: SelectOption<number>[] = useMemo(
    () => [
      {
        label: t("ProjectHistory.Filter.Last30Days"),
        value: 30
      },
      {
        label: t("ProjectHistory.Filter.Last60Days"),
        value: 60
      },
      {
        label: t("ProjectHistory.Filter.Last90Days"),
        value: 90
      }
    ],
    [t]
  )

  return {
    form,
    timeFrameOptions
  }
}
